import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "../../../components/ui/button";
import { Card, CardContent } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { AdminPageHeader, StateBlock, StatusBadge } from "../components/AdminUi";
import { BRANDS, socialApi } from "./socialMarketingApi";

const BRAND_MODULE = {
  affidya: "social-marketing-affidya",
  casadiriposoperte: "social-marketing-casadiriposoperte",
  rco: "social-marketing-rco",
};

const ENV_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

function openModule(moduleId) {
  const params = new URLSearchParams(window.location.search);
  params.set("module", moduleId);
  params.delete("tab");
  window.location.assign(`${window.location.pathname}?${params.toString()}`);
}

function isLocalUrl(value) {
  try {
    const host = new URL(value).hostname;
    return host === "localhost" || host === "127.0.0.1" || host.endsWith(".local");
  } catch {
    return false;
  }
}

function normalizeOrigin(value) {
  return String(value || "").trim().replace(/\/+$/, "").replace(/\/api\/social-marketing\/bot$/, "").replace(/\/api$/, "");
}

function tokenForBrand(config, brandId) {
  const row = (config?.bot_brand_tokens || []).find((item) => item.brand === brandId);
  if (row?.token) return row.token;
  return config?.bot_token || "";
}

function botText({ label, brand, base, token }) {
  const secret = token || "INCOLLA_QUI_IL_TOKEN";
  const root = `${base}/api/social-marketing/bot`;
  return `Name: Marketing ${label}
Title: Prepara i post di ${label} e inviali ad Affidya
Description:
Sei il bot marketing del marchio ${label}. Scrivi i post social e inviali ad Affidya per la revisione. Non pubblicarli prima dell'approvazione.

Indirizzo: ${root}
Token: ${secret}
Ogni chiamata usa l'header Authorization: Bearer ${secret}
Puoi farle con curl dal tuo computer.

Quando ti chiedono un post:
1. Leggi le indicazioni del marchio: GET ${root}/brief?brand=${brand}
2. Scrivi il post seguendo quel testo. Non inserire dati di ospiti, pazienti o familiari.
3. Invia un elemento in variants per ogni social, con il testo e gli URL https di immagini o video di quel canale. POST ${root}/posts con questo JSON:
{"brand":"${brand}","title":"titolo interno","external_ref":"${brand}-data-1","scheduled_at":"2027-01-15T10:00:00+01:00","variants":[{"platform":"instagram","content":"didascalia","media_urls":["https://esempio/foto.jpg"]},{"platform":"facebook","content":"testo","media_urls":["https://esempio/foto.jpg"]},{"platform":"linkedin","content":"testo","media_urls":["https://esempio/video.mp4"]},{"platform":"x","content":"testo","media_urls":[]},{"platform":"tiktok","content":"testo","media_urls":["https://esempio/video.mp4"]},{"platform":"youtube","content":"testo","media_urls":["https://esempio/video.mp4"]}]}
platform può essere instagram, facebook, linkedin, x, tiktok, youtube, threads, pinterest. Senza variants Affidya mostra solo un testo generico, senza anteprima del social.
4. external_ref è un codice tuo, stabile per quel post. Se lo riusi mentre il post è ancora in revisione, Affidya aggiorna lo stesso post.
5. Se la risposta contiene "status":"in_revisione" e variants con i canali, il materiale è arrivato. Fermati e conferma l'invio.

Affidya può anche scriverti da sola. Nella tua routine aggiungi un trigger webhook. L'operatore incolla indirizzo e chiave nella pagina di ${label}, nel riquadro «Chiedi un post al bot».

Quando parte il webhook, il corpo JSON contiene cosa_fare, istruzione_operatore, external_ref e, se è una modifica, post_attuale. Fai esattamente quello che dice cosa_fare:
- evento social_marketing.crea_post: genera un post nuovo, con testo e URL https di immagini o video per ogni social, e caricalo in revisione. Usa l'external_ref indicato.
- evento social_marketing.modifica_post: il post è già in revisione. Cambia testo, immagini o video come chiede istruzione_operatore. Il resto resta uguale a post_attuale. Ricarica tutte le variants con POST ${root}/posts, lo stesso brand e lo stesso external_ref: Affidya aggiorna quella versione e non ne crea un'altra.
Poi conferma con POST all'indirizzo dove_confermare indicato nel corpo e {"status":"completato"}.
Se il corpo non basta, leggi i lavori aperti: GET ${root}/tasks?brand=${brand}
Non pubblicare questi post.

Dopo l'approvazione di un operatore, a intervalli:
1. GET ${root}/approved?brand=${brand}
2. GET ${root}/config per leggere la chiave e i canali Postiz. La chiave va nell'header Authorization di Postiz, senza la parola Bearer.
3. Per ogni post approvato, invia a Postiz il corpo postiz_request.
4. Poi POST ${root}/posts/ID/ack con {"revision": numero letto, "status":"programmato", "postiz_post_ids":["id restituito da Postiz"]}.
5. Se lo stato non è più "approvato", non pubblicare.`;
}

export default function SocialMarketingProcedureModule() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [config, setConfig] = useState(null);
  const [prompts, setPrompts] = useState({});
  const [address, setAddress] = useState(ENV_BASE);
  const [brand, setBrand] = useState("affidya");
  const [busy, setBusy] = useState("");

  const load = () => {
    setLoading(true);
    setError("");
    return Promise.all([
      socialApi("/postiz"),
      ...Object.keys(BRANDS).map((id) => socialApi(`/prompts/${id}`)),
    ]).then(([postiz, ...brandPrompts]) => {
      setConfig(postiz);
      setAddress(postiz.public_api_base || ENV_BASE);
      const next = {};
      brandPrompts.forEach((item) => { next[item.brand] = item.prompt || ""; });
      setPrompts(next);
    }).catch((err) => {
      setError(err.message || "Impossibile caricare la procedura");
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const base = normalizeOrigin(address);
  const local = isLocalUrl(base);
  const text = useMemo(
    () => botText({
      label: BRANDS[brand].label,
      brand,
      base: base || "https://INDIRIZZO-AFFIDYA",
      token: tokenForBrand(config, brand),
    }),
    [brand, base, config],
  );

  const run = async (key, action) => {
    setBusy(key);
    try {
      await action();
    } catch (err) {
      toast.error(err.message || "Operazione non riuscita");
    } finally {
      setBusy("");
    }
  };

  const saveAddress = () => run("address", async () => {
    const data = await socialApi("/postiz", {
      method: "PUT",
      body: JSON.stringify({ public_api_base: normalizeOrigin(address) }),
    });
    setConfig(data);
    setAddress(data.public_api_base || "");
    toast.success("Indirizzo salvato. È quello scritto nelle istruzioni da copiare.");
  });

  const regenerateShared = () => run("token", async () => {
    if (config?.bot_token_set && !window.confirm("Il token condiviso precedente smette subito di funzionare. I bot che lo usano già, come Affidya e Casadiriposoperte, vanno aggiornati. I token di un solo marchio restano validi. Generare un token nuovo per tutti i marchi?")) return;
    const data = await socialApi("/bot-token", { method: "POST" });
    setConfig(data);
    toast.success("Token condiviso pronto. È scritto nelle istruzioni dei marchi che non hanno un token dedicato.");
  });

  const regenerateBrand = (brandId) => run(`token-${brandId}`, async () => {
    const label = BRANDS[brandId].label;
    const existing = (config?.bot_brand_tokens || []).find((item) => item.brand === brandId && item.set);
    if (existing && !window.confirm(`Il token precedente di ${label} smette di funzionare. Il token condiviso e i token degli altri marchi restano validi. Continuare?`)) return;
    const data = await socialApi(`/bot-token?brand=${encodeURIComponent(brandId)}`, { method: "POST" });
    setConfig(data);
    setBrand(brandId);
    toast.success(`Token di ${label} pronto. Resta visibile in questa pagina: copialo nel bot di quel marchio.`);
  });

  const copy = async () => {
    if (!tokenForBrand(config, brand)) {
      toast.error(`Il token di ${BRANDS[brand].label} non è visibile. Genera un token solo per questo marchio: gli altri bot restano collegati.`);
      return;
    }
    if (!base || local) {
      toast.error("Indica l'indirizzo pubblico HTTPS di Affidya. Un bot nel cloud non raggiunge localhost.");
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Istruzioni copiate. Incollale nelle impostazioni del bot Grok.");
    } catch {
      toast.error("Seleziona il testo e copialo a mano.");
    }
  };

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Procedura bot"
        description="Come collegare i bot marketing di Grok perché inviino ad Affidya i post che generano."
      />

      <Card>
        <CardContent className="space-y-4 p-6">
          <h3 className="font-medium text-admin-text">Cinque passi</h3>
          <ol className="list-decimal space-y-3 pl-5 text-sm leading-relaxed text-admin-text">
            <li>Usa il token visibile qui sotto. Quello condiviso vale per i tre marchi: se ne resta solo l'anteprima, non generarne uno nuovo, perché i bot già collegati smetterebbero di entrare. Per un marchio ancora da collegare genera un token solo per quel marchio: resta visibile in questa pagina e non tocca gli altri.</li>
            <li>Apri <button type="button" className="font-medium text-admin-accent underline" onClick={() => openModule("social-marketing-affidya")}>Affidya</button>, <button type="button" className="font-medium text-admin-accent underline" onClick={() => openModule("social-marketing-casadiriposoperte")}>Casadiriposoperte</button> e <button type="button" className="font-medium text-admin-accent underline" onClick={() => openModule("social-marketing-rco")}>R&CO Group</button>. In ogni pagina scrivi le indicazioni (tono, temi, cosa evitare) e salva. Il bot le legge prima di scrivere.</li>
            <li>In <button type="button" className="font-medium text-admin-accent underline" onClick={() => openModule("social-marketing-postiz")}>Postiz</button> inserisci la chiave API e i canali di ogni marchio. Servono dopo l'approvazione, quando il bot pubblica. Per il solo invio del testo ad Affidya non servono. Se in <button type="button" className="font-medium text-admin-accent underline" onClick={() => openModule("social-marketing-meta")}>Collegamento Meta</button> il marchio è attivo, Facebook e Instagram li pubblica Affidya all'orario approvato: il bot non deve inviarli di nuovo.</li>
            <li>Su Grok Bot crea un bot per marchio. In Settings, in alto a destra, trovi Name, Title e Description: puoi anche dettarli in chat. Incolla il testo di questo marchio. In Settings, General, Agent consenti le chiamate verso l'indirizzo di Affidya e verso api.postiz.com.</li>
            <li>Scrivi al bot: «Prepara un post per ogni social, con immagini o video, e invialo ad Affidya». Oppure, nella pagina del marchio, salva la routine webhook e usa «Invia al bot» per un post nuovo, o «Invia modifica al bot» su un post già in revisione: il bot cambia testo, immagini o video e aggiorna quella versione. Poi apri la voce del marchio: il post è In revisione e, per ogni canale, c'è l'anteprima.</li>
          </ol>
        </CardContent>
      </Card>

      {loading ? <StateBlock state="loading" title="Controllo di cosa è già pronto…" /> : null}
      {error ? <StateBlock state="error" title="Impossibile caricare lo stato" description={error} onRetry={load} /> : null}

      {!loading && !error ? (
        <>
          <Card>
            <CardContent className="space-y-3 p-6">
              <h3 className="font-medium text-admin-text">Cosa è già pronto</h3>
              <div className="flex flex-wrap gap-2">
                <StatusBadge tone={config?.bot_token_set ? "success" : "warning"}>{config?.bot_token_set ? "Token bot presente" : "Token bot da generare"}</StatusBadge>
                <StatusBadge tone={config?.api_key_set ? "success" : "neutral"}>{config?.api_key_set ? "Chiave Postiz presente" : "Chiave Postiz ancora vuota"}</StatusBadge>
                {Object.values(BRANDS).map((item) => (
                  <StatusBadge key={item.id} tone={(prompts[item.id] || "").trim() ? "success" : "warning"}>
                    {(prompts[item.id] || "").trim() ? `Indicazioni ${item.label} scritte` : `Indicazioni ${item.label} vuote`}
                  </StatusBadge>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-3 p-6">
              <h3 className="font-medium text-admin-text">1. Indirizzo che il bot deve chiamare</h3>
              <p className="text-sm text-admin-muted">Solo l'indirizzo del server, senza percorso. Esempio: https://api.tuodominio.it</p>
              <div className="flex flex-wrap items-end gap-2">
                <div className="min-w-[16rem] flex-1 space-y-1">
                  <Label htmlFor="public-base">Indirizzo</Label>
                  <Input id="public-base" value={address} onChange={(event) => setAddress(event.target.value)} placeholder="https://" />
                </div>
                <Button type="button" variant="outline" onClick={saveAddress} disabled={busy === "address"}>Salva indirizzo</Button>
              </div>
              {local ? (
                <p className="text-sm text-admin-text" role="status">Questo indirizzo è locale. Il bot Grok lavora nel cloud e da lì non raggiunge localhost: inserisci l'indirizzo pubblico HTTPS di Affidya.</p>
              ) : null}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-4 p-6">
              <h3 className="font-medium text-admin-text">2. Token del bot</h3>
              <p className="text-sm text-admin-muted">
                Il token condiviso vale per Affidya, Casadiriposoperte e R&CO Group.
                {config?.bot_token
                  ? " Il valore è qui sotto e resta in questa pagina."
                  : config?.bot_token_preview
                    ? ` Quello già in uso (${config.bot_token_preview}) non si può più leggere. Generarne uno nuovo scollega i bot che lo usano già.`
                    : " Non è ancora stato generato."}
              </p>
              {config?.bot_token ? (
                <div className="space-y-1">
                  <Label htmlFor="procedure-token">Token condiviso</Label>
                  <Input id="procedure-token" readOnly value={config.bot_token} onFocus={(event) => event.target.select()} />
                </div>
              ) : null}
              <Button type="button" variant="outline" onClick={regenerateShared} disabled={Boolean(busy)}>
                {config?.bot_token_set ? "Genera un token nuovo per tutti i marchi" : "Genera token condiviso"}
              </Button>
              <div className="space-y-3 border-t border-admin-border pt-4">
                <p className="text-sm text-admin-muted">
                  Un token di un solo marchio entra soltanto per quel marchio e non scollega gli altri bot. Per aggiungere R&CO Group senza toccare Affidya e Casadiriposoperte, genera il token qui sotto: resta visibile e finisce nel testo da incollare.
                </p>
                {Object.values(BRANDS).map((item) => {
                  const row = (config?.bot_brand_tokens || []).find((entry) => entry.brand === item.id);
                  const pending = busy === `token-${item.id}`;
                  return (
                    <div key={item.id} className="space-y-2 rounded-lg border border-admin-border p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <div className="text-sm font-medium text-admin-text">{item.label}</div>
                          <div className="text-xs text-admin-muted">
                            {row?.token
                              ? "Token dedicato visibile qui sotto. Vale solo per questo marchio."
                              : row?.set
                                ? `Token dedicato attivo (${row.preview}), valore non più leggibile.`
                                : "Nessun token dedicato: il bot usa quello condiviso, se è visibile."}
                          </div>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant={item.id === "rco" && !row?.token ? "default" : "outline"}
                          onClick={() => regenerateBrand(item.id)}
                          disabled={Boolean(busy)}
                        >
                          {pending ? "Generazione…" : row?.set ? `Nuovo token solo per ${item.label}` : `Genera token solo per ${item.label}`}
                        </Button>
                      </div>
                      {row?.token ? (
                        <Input
                          readOnly
                          aria-label={`Token ${item.label}`}
                          value={row.token}
                          onFocus={(event) => event.target.select()}
                        />
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-3 p-6">
              <h3 className="font-medium text-admin-text">3. Testo da incollare nel bot Grok</h3>
              <p className="text-sm text-admin-muted">Scegli il marchio, copia e incolla tutto nelle impostazioni di quel bot, oppure nella chat. Name, Title e Description sono già nel testo, con i nomi che vedi su Grok.</p>
              <div className="flex flex-wrap gap-2">
                {Object.values(BRANDS).map((item) => (
                  <Button key={item.id} type="button" size="sm" variant={brand === item.id ? "default" : "outline"} onClick={() => setBrand(item.id)}>
                    {item.label}
                  </Button>
                ))}
              </div>
              <textarea
                readOnly
                aria-label={`Istruzioni per il bot ${BRANDS[brand].label}`}
                className="min-h-[22rem] w-full rounded-lg border border-admin-border bg-admin-surface p-4 font-mono text-xs leading-relaxed text-admin-text"
                value={text}
                onFocus={(event) => event.target.select()}
              />
              <Button type="button" onClick={copy}>Copia istruzioni</Button>
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}
